<template>
  <div class="d-flex flex-column" style="gap: 2rem">
    <template v-if="isLoading">
      <main-loader />
    </template>
    <template v-else>
      <v-card elevation="2" class="pa-4">
        <rzi-joined-table
          :data="{ type: 'geometries', features: cardTables.geometries ?? [] }"
          :is-drawing-on.sync="computedIsDrawingOn"
          :sticky-buttons.sync="computedStickyButtons"
          :current-metadata="currentMetadata"
          :current-site="item.id"
          upload-data
          :upload-data-props="{
            staticProperties: {
              [currentMetadata.tableViewsSharedData.filterAttribute]: item.id,
            },
          }"
          @refreshTableDatasources="handleRefreshGeometries"
        />
      </v-card>
      <v-card elevation="2" class="pa-4 text-left d-flex flex-column" style="gap: 1rem;">
        <v-row no-gutters>
          <v-col>
            <span class="font-weight-bold">{{ $i18n.t('table.rziMonitoring.protectionForms') }}</span>
          </v-col>
        </v-row>
        <v-row no-gutters class="d-flex flex-column" style="gap: 0.3rem;">
          <template v-if="formsList.length">
            <v-tooltip v-for="(form, index) in formsList" :key="index" bottom color="font">
              <template #activator="{ on }">
                <v-col>
                  <span v-on="on"> {{ form.type }}: {{ form.name }} </span>
                </v-col>
              </template>
              {{ form.code }}
            </v-tooltip>
          </template>
          <template v-else>
            <v-col>
              <span>-</span>
            </v-col>
          </template>
        </v-row>
      </v-card>
      <v-card elevation="2" class="pa-4">
        <v-row no-gutters class="mb-4">
          <v-col cols="auto" class="ml-auto">
            <base-rounded-button
              color="primary"
              size="x-small"
              icon="mdi-pencil"
              icon-color="#fff"
              @click="isEditFeatureLoaded = isEditFeatureVisible = true"
            />
          </v-col>
        </v-row>
        <v-row no-gutters v-for="(row, idx) in formSchema" :key="idx" style="gap: 1rem;">
          <v-col v-for="(element, index) in row" :key="index" class="d-flex align-baseline" style="gap: 0.5rem">
            <div style="flex-grow: 1;">
              <feature-card-data-row
                v-bind="{
                  ...element,
                  ...(element.dataType?.name === 'text' ? { wrapText: true } : {}),
                }"
                v-model="values[element.attribute]"
                dynamic-icon-column
                wrap-text
                :disable-width-limit="true"
              />
            </div>
            <div v-if="element.attribute === currentMetadata.monitoringYears.attributes.area" cols="auto">
              {{ element.unit }}
            </div>
          </v-col>
        </v-row>
      </v-card>
      <v-card
        v-for="(table, index) in Object.keys(currentMetadata.tableViews).filter(
          tb => ![...currentMetadata.tableViewsSharedData.lowerSectionTables, 'geometries'].includes(tb)
        )"
        :key="index"
        elevation="2"
        class="pa-4"
      >
        <rzi-joined-table
          :data="{ type: table, features: cardTables[table] ?? [] }"
          :is-drawing-on.sync="computedIsDrawingOn"
          :sticky-buttons.sync="computedStickyButtons"
          :current-metadata="currentMetadata"
          :current-site="item.id"
          @refreshTableDatasources="handleRefreshGeometries"
        />
      </v-card>
      <v-row no-gutters>
        <v-col>
          <data-input
            v-model="resignationData.resign"
            :label="$i18n.t('table.rziMonitoring.resign')"
            :data-type="{ name: 'checkbox' }"
            class="resignation-checkbox"
            hide-details
            @input="toggleResignation"
          />
        </v-col>
      </v-row>
      <v-row v-if="resignationData.resign && resignationTextInput" no-gutters>
        <v-col cols="9">
          <feature-card-data-row
            v-bind="resignationTextInput"
            v-model="resignationData.resignDesc"
            @change="changeResignationDescription"
          />
        </v-col>
      </v-row>
      <div
        class="d-flex flex-column"
        :class="{ 'rzi-monitoring-hidden-section': resignationData.resign }"
        style="gap: 2rem"
      >
        <v-card
          v-for="(table, index) in currentMetadata.tableViewsSharedData.lowerSectionTables"
          :key="index"
          elevation="2"
          class="pa-4"
        >
          <rzi-joined-table
            :data="{ type: table, features: cardTables[table] ?? [] }"
            :is-drawing-on.sync="computedIsDrawingOn"
            :sticky-buttons.sync="computedStickyButtons"
            :current-metadata="currentMetadata"
            :current-site="item.id"
            @refreshTableDatasources="handleRefreshGeometries"
          />
        </v-card>
        <v-card elevation="2" class="pa-4">
          <rzi-photos
            :current-metadata="currentMetadata"
            :item="item"
            @retrievePhotos="retrievePhotos"
            :photos.sync="photos"
          />
        </v-card>
        <v-card elevation="2" class="pa-4">
          <rzi-fito
            :current-metadata="currentMetadata"
            :item="item"
            :fitos.sync="fitos"
            :is-drawing-on.sync="computedIsDrawingOn"
            :sticky-buttons.sync="computedStickyButtons"
          />
        </v-card>
        <v-card elevation="2" class="pa-4">
          <rzi-scores
            :current-metadata="currentMetadata"
            :data-proxy="computedScoresData"
            :item="item"
            @retrieveItem="handleEdit"
            @retrieveScores="retrieveScores"
          />
        </v-card>
      </div>
      <add-edit-feature-dialog
        v-if="isEditFeatureLoaded"
        :is-visible.sync="isEditFeatureVisible"
        :title="$i18n.t('table.rziMonitoring.editMonitoring')"
        v-bind="getEditFeatureData()"
        @done="handleEdit"
      />
    </template>
  </div>
</template>

<script>
import { get, call, sync } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import FeatureCardDataRow from '@/components/FeatureCardDataRow';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import RziJoinedTable from '@/components/RziJoinedTable';
import MainLoader from '@/components/MainLoader';
import RziPhotos from '@/components/RziPhotos';
import DataInput from '@/components/DataInput';
import RziScores from '@/components/RziScores';
import RziFito from '@/components/RziFito';
import { sitesTypes as sitesTypesMetadata } from '@/assets/js/rziData';

import { utils } from '@/mixins/rziHelpers';

export default {
  name: 'RziMonitoringYearSection',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    FeatureCardDataRow,
    AddEditFeatureDialog,
    RziJoinedTable,
    MainLoader,
    RziPhotos,
    DataInput,
    RziScores,
    RziFito,
  },
  props: {
    currentMetadata: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    mainItem: {
      type: Object,
      required: true,
    },
    isDrawingOn: {
      type: Boolean,
    },
    stickyButtons: {
      type: Array,
    },
  },
  data: () => ({
    isEditFeatureLoaded: false,
    isEditFeatureVisible: false,
    isLoading: true,
    resignationData: {
      resign: false,
      resignDesc: null,
    },
    formSchema: [],
    formsList: [],
    values: {},
    photos: [],
    fitos: [],
    scores: null,
    cardTables: {},
  }),
  computed: {
    sitesTypesFeatures: sync('rziData/sitesTypesFeatures'),
    rziScores: sync('rziData/scores'),
    layersMetadata: get('layers/metadata'),
    computedScoresData() {
      return {
        ...this.scores,
        main: this.getMainScoreData(),
      };
    },
    computedIsDrawingOn: {
      get() {
        return this.isDrawingOn;
      },
      set(value) {
        this.$emit('update:isDrawingOn', value);
      },
    },
    computedStickyButtons: {
      get() {
        return this.stickyButtons;
      },
      set(value) {
        this.$emit('update:stickyButtons', value);
      },
    },
    resignationTextInput() {
      const formSchemaMetadata = this.proxyFormSchema.find(
        attr => attr.attribute === this.currentMetadata.monitoringYears.attributes.resignDesc
      );
      if (!formSchemaMetadata) return null;
      return {
        ...formSchemaMetadata,
        wrapText: true,
        disableWidthLimit: true,
        dynamicIconColumn: true,
        isEditingOn: true,
      };
    },
  },
  methods: {
    getProtectionForms: call('rziData/getProtectionForms'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    getEditFeatureData() {
      const currentDatasource = this.currentMetadata.monitoringYears.datasource;
      return {
        featureData: {
          id: this.item.id,
          datasource: currentDatasource,
        },
        featureMetadata: this.item,
        hiddenAttributes: this.layersMetadata[currentDatasource]?.attributes_schema?.attributes
          ?.filter(attr => !this.currentMetadata.monitoringYears.featureCardAppearance.flat().includes(attr.name))
          .map(attr => attr.name),
      };
    },
    getMainScoreData() {
      return ['score', 'scoreDesc', 'scoreDate'].reduce((acc, item) => {
        acc[this.currentMetadata.monitoringYears.attributes[item]] = this.item.properties[
          this.currentMetadata.monitoringYears.attributes[item]
        ];
        return acc;
      }, {});
    },
    handleEdit(closeDialog) {
      this.$emit('refreshMonitoringYears', () => {
        this.retrieveFormSchema();
        closeDialog();
      });
    },
    async manipulateResignation(body) {
      try {
        await this.editNonTopoFeatureAction({
          datasource: this.currentMetadata.monitoringYears.datasource,
          id: this.item.id,
          body,
        });
        this.handleEdit(() => {});
      } catch {
        //
      }
    },
    async changeResignationDescription(e) {
      if (e === this.proxyResignationData.resignDesc || !e) return;
      await this.manipulateResignation({
        [this.currentMetadata.monitoringYears.attributes.resign]: this.resignationData.resign,
        [this.currentMetadata.monitoringYears.attributes.resignDesc]: e,
      });
    },
    async toggleResignation(e) {
      if (this.proxyResignationData.resign === e) return;
      if (!e) {
        await this.manipulateResignation({
          [this.currentMetadata.monitoringYears.attributes.resign]: false,
          [this.currentMetadata.monitoringYears.attributes.resignDesc]: null,
        });
      }
    },
    retrieveFormSchema() {
      this.resignationData = {
        resign: this.item.properties[this.currentMetadata.monitoringYears.attributes.resign],
        resignDesc: this.item.properties[this.currentMetadata.monitoringYears.attributes.resignDesc],
      };
      this.proxyResignationData = JSON.parse(JSON.stringify(this.resignationData));
      const formSchema = this.getForm(
        null,
        this.layersMetadata[this.currentMetadata.monitoringYears.datasource].attributes_schema.attributes
      );
      this.proxyFormSchema = formSchema;
      this.formSchema = this.currentMetadata.monitoringYears.featureCardAppearance.reduce((acc, item) => {
        acc.push(item.map(itm => formSchema.find(el => el.attribute === itm)));
        return acc;
      }, []);
      const feature = this.item;
      const idAttributeName = this.layersMetadata[this.currentMetadata.monitoringYears.datasource].attributes_schema
        .id_name;
      const properties = this.getFlattenProperties(JSON.parse(JSON.stringify(feature)), idAttributeName);
      this.values = this.getValues(formSchema, properties);
      this.valuesCopy = JSON.parse(JSON.stringify(this.values));
    },
    async handleRefreshGeometries(datasourcesToRefresh, callback) {
      this.formsList = await this.getProtectionForms(this.item.id);
      this.handleRefreshTableDatasources(datasourcesToRefresh, callback);
    },
    async retrievePhotos(callback) {
      try {
        const photos = await this.getDataSourceFeatures({
          dataSource: this.currentMetadata.photos.datasource,
          filters: {
            $EQUAL: {
              [`${this.currentMetadata.photos.datasource}.${this.currentMetadata.photos.attributes.mainId}`]: {
                value: this.item.id,
              },
            },
          },
        });
        this.photos = photos.features;
        callback();
      } catch {
        //
      }
    },
    async init() {
      this.isLoading = true;
      try {
        this.retrieveFormSchema();
        const tablesDatasources = Object.values(this.currentMetadata.tableViews).reduce((acc, item) => {
          Object.entries(item.datasources).forEach(([type, dt]) => {
            let founditem = acc.find(it => it[1] === dt);
            if (!founditem) {
              acc.push([type, dt]);
            }
          });
          return acc;
        }, []);
        const promises = tablesDatasources.reduce((acc, datasource) => {
          acc = [...acc, this.getSingleDatasourceFeatures(datasource[1], datasource[0])];
          return acc;
        }, []);
        const [scores, sitesTypes, formsList, photos, fitos, ...results] = await Promise.all([
          ...(this.rziScores.length
            ? [new Promise(resolve => resolve(null))]
            : [this.getDataSourceFeatures({ dataSource: this.currentMetadata.score.dictGiosScores.datasource })]),
          ...(this.sitesTypesFeatures.length
            ? [new Promise(resolve => resolve(null))]
            : [this.getDataSourceFeatures({ dataSource: sitesTypesMetadata.metadata.datasource })]),
          this.getProtectionForms(this.item.id),
          this.getDataSourceFeatures({
            dataSource: this.currentMetadata.photos.datasource,
            filters: {
              $EQUAL: {
                [`${this.currentMetadata.photos.datasource}.${this.currentMetadata.photos.attributes.mainId}`]: {
                  value: this.item.id,
                },
              },
            },
          }),
          this.getDataSourceFeatures({
            dataSource: this.currentMetadata.fito.datasource,
            filters: {
              $EQUAL: {
                [`${this.currentMetadata.fito.datasource}.${this.currentMetadata.fito.attributes.year}`]: {
                  value: this.item.id,
                },
              },
            },
          }),
          ...promises,
        ]);
        if (sitesTypes) this.sitesTypesFeatures = sitesTypes.features;
        if (scores)
          this.rziScores = {
            scores,
            currentMetadata: this.currentMetadata,
          };
        this.photos = photos.features;
        this.fitos = fitos.features;
        this.formsList = formsList;
        this.cardTables = results.flat().reduce((acc, item) => {
          acc[item[0]] = acc[item[0]] ? [...acc[item[0]], ...item[1]] : [...item[1]];
          return acc;
        }, {});
        await this.retrieveScores();
        this.isLoading = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
:deep(.feature-card-data-sidebar-row) {
  padding: 0 !important;
}

.resignation-checkbox :deep(.v-label) {
  color: #000 !important;
}

.rzi-monitoring-hidden-section {
  position: relative;
}
.rzi-monitoring-hidden-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba($color: #ffffff, $alpha: 0.7);
  z-index: 2;
}
</style>
